import * as React from "react";
import { useCallback, useMemo } from "react";
import { defaultCompare } from "logic/DefaultCompare";
import { ClearFloat, Select } from "components/common";
import { Col, ControlLabel, FormGroup, HelpBlock, Row } from "components/bootstrap";
import { HelpPanel } from "components/event-definitions/common/HelpPanel";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import usePluginEntities from "hooks/usePluginEntities";
import styles from "./EventConditionForm.css";
import commonStyles from "../common/commonStyles.css";
import { SYSTEM_EVENT_DEFINITION_TYPE } from "../constants";
const ConditionTypeDescriptions = ({ eventDefinitionTypes }) => {
  const typeDescriptions = eventDefinitionTypes.map((type) => /* @__PURE__ */ React.createElement(React.Fragment, { key: type.type }, /* @__PURE__ */ React.createElement("dt", null, type.displayName), /* @__PURE__ */ React.createElement("dd", null, type.description || "No description available.")));
  return /* @__PURE__ */ React.createElement("dl", null, typeDescriptions);
};
const EventConditionForm = ({
  action = "create",
  entityTypes = void 0,
  eventDefinition,
  validation,
  currentUser,
  onChange,
  canEdit
}) => {
  var _a, _b, _c;
  const sendTelemetry = useSendTelemetry("event-definition-condition");
  const eventDefinitionTypes = usePluginEntities("eventDefinitionTypes");
  const filteredDefinitionTypes = eventDefinitionTypes.filter((type) => type.useCondition() && !type.hideFromCreation);
  const currentConditionPlugin = useMemo(
    () => eventDefinitionTypes.find((edt) => edt.type === eventDefinition.config.type),
    [eventDefinitionTypes, eventDefinition.config.type]
  );
  const getConditionPlugin = useCallback(
    (type) => {
      if (type === void 0) {
        return void 0;
      }
      return filteredDefinitionTypes.find((eventDefinitionType) => eventDefinitionType.type === type);
    },
    [filteredDefinitionTypes]
  );
  const sortedEventDefinitionTypes = useMemo(
    () => filteredDefinitionTypes.sort((eventDefinitionType1, eventDefinitionType2) => {
      const eventDefinitionType1Order = eventDefinitionType1.sortOrder;
      const eventDefinitionType2Order = eventDefinitionType2.sortOrder;
      if (eventDefinitionType1Order !== void 0 || eventDefinitionType2Order !== void 0) {
        const sort = (eventDefinitionType1Order != null ? eventDefinitionType1Order : Number.MAX_SAFE_INTEGER) - (eventDefinitionType2Order != null ? eventDefinitionType2Order : Number.MAX_SAFE_INTEGER);
        if (sort !== 0) {
          return sort;
        }
      }
      return defaultCompare(eventDefinitionType1.displayName, eventDefinitionType2.displayName);
    }),
    [filteredDefinitionTypes]
  );
  const formattedEventDefinitionTypes = useMemo(() => {
    const options = sortedEventDefinitionTypes.map((type) => ({ label: type.displayName, value: type.type }));
    if (currentConditionPlugin && !options.some((o) => o.value === currentConditionPlugin.type)) {
      options.push({ label: currentConditionPlugin.displayName, value: currentConditionPlugin.type });
    }
    return options;
  }, [sortedEventDefinitionTypes, currentConditionPlugin]);
  const handleEventDefinitionTypeChange = (nextType) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.TYPE_SELECTED, {
      app_action_value: "type-select",
      condition_type: nextType
    });
    const conditionPlugin = getConditionPlugin(nextType);
    const defaultConfig = (conditionPlugin == null ? void 0 : conditionPlugin.defaultConfig) || {};
    onChange("config", { ...defaultConfig, type: nextType });
  };
  const disabledSelect = useMemo(
    () => !formattedEventDefinitionTypes.some((edt) => eventDefinition.config.type === edt.value) && action === "edit",
    [action, eventDefinition.config.type, formattedEventDefinitionTypes]
  );
  const onlyFilters = useMemo(
    () => eventDefinition._scope === "ILLUMINATE" && action === "edit",
    [action, eventDefinition._scope]
  );
  const isNonCreatableType = useMemo(
    () => action === "edit" && !!(currentConditionPlugin == null ? void 0 : currentConditionPlugin.hideFromCreation),
    [action, currentConditionPlugin]
  );
  const isSystemEventDefinition = eventDefinition.config.type === SYSTEM_EVENT_DEFINITION_TYPE;
  const canEditCondition = canEdit && !isSystemEventDefinition;
  const eventDefinitionTypeComponent = (currentConditionPlugin == null ? void 0 : currentConditionPlugin.formComponent) ? React.createElement(currentConditionPlugin.formComponent, {
    action,
    entityTypes,
    currentUser,
    validation,
    eventDefinition,
    onChange,
    key: eventDefinition.id
  }) : null;
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Event Condition"), !canEditCondition ? /* @__PURE__ */ React.createElement("p", null, "The conditions of this event definition type cannot be edited.") : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Define how Events of this type should be created. You can later use those Events as input on other Conditions, making it possible to build powerful Conditions based on others."), /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.config ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, { htmlFor: "event-condition-type-select" }, "Condition Type"), /* @__PURE__ */ React.createElement(
    Select,
    {
      placeholder: "Select a Condition Type",
      inputId: "event-condition-type-select",
      options: formattedEventDefinitionTypes,
      value: eventDefinition.config.type,
      onChange: handleEventDefinitionTypeChange,
      clearable: false,
      disabled: disabledSelect || onlyFilters || isNonCreatableType,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, (_c = (_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.config) == null ? void 0 : _b[0]) != null ? _c : "Choose the type of Condition for this Event.")))), canEditCondition && !disabledSelect && /* @__PURE__ */ React.createElement(Col, { md: 5, lg: 5, lgOffset: 1 }, /* @__PURE__ */ React.createElement(HelpPanel, { className: styles.conditionTypesInfo, title: "Available Conditions" }, /* @__PURE__ */ React.createElement(ConditionTypeDescriptions, { eventDefinitionTypes: sortedEventDefinitionTypes }))), /* @__PURE__ */ React.createElement(ClearFloat, null), canEditCondition && eventDefinitionTypeComponent && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("hr", { className: styles.hr }), /* @__PURE__ */ React.createElement(Col, { md: 12 }, eventDefinitionTypeComponent)));
};
export default EventConditionForm;
